import React, { useEffect, useMemo } from 'react'
import { Typography, makeStyles, IconButton } from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import StarIcon from '@material-ui/icons/Star'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useIntl } from 'react-intl'

import { studentCourseActions } from '../../../_state/actions'
import { CapeeshColors } from '../../../assets/ColorPalette'
import { history } from '../../../_state/helpers'
import DefaultCourseGroupImage from '../../../assets/images/courseGroup/FallbackCourseGroupImage.jpeg'
import LessonCard from './LessonCard'
import { useStudentImage } from '../useStudentImage'
import { studentScreenMessages } from '../../../Views/StudentPrivate/StudentScreen.i18n.Messages'

import type {
    CourseMetadataV1Dto,
    CourseLessonOrderDto,
    CourseLessonOrderItemDto,
    CourseLessonOrderProgressionDto,
    CourseLessonOrderItemProgressionDto,
} from '../../../Logic/StudentCourseDtos'

// ─── Styles ───────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column' as const,
        height: '100%',
    },

    // ── Navigation bar ──────────────────────────────
    navBar: {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px 8px 12px',
        position: 'sticky' as const,
        top: 0,
        backgroundColor: '#FFFFFF',
        zIndex: 1,
        '@media (max-width: 599px)': {
            padding: '12px 12px 8px 8px',
        },
    },
    backButton: {
        color: CapeeshColors.DarkText,
        '&:hover': {
            backgroundColor: CapeeshColors.Gray5,
        },
    },
    navStarBadge: {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        gap: 4,
    },
    navStarText: {
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 600,
        color: CapeeshColors.DarkText,
    },
    navStarIcon: {
        fontSize: 20,
        color: CapeeshColors.YellowBright,
    },

    // ── Topic header hero ───────────────────────────
    heroSection: {
        margin: '0 20px 12px 20px',
        borderRadius: 16,
        overflow: 'hidden',
        position: 'relative' as const,
        backgroundColor: CapeeshColors.DeepBlue,
        minHeight: 140,
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'flex-end',
        '@media (max-width: 599px)': {
            margin: '0 12px 12px 12px',
            minHeight: 120,
            borderRadius: 12,
        },
    },
    heroImage: {
        position: 'absolute' as const,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
        opacity: 0.3,
    },
    heroContent: {
        position: 'relative' as const,
        zIndex: 1,
        padding: '20px 20px 18px 20px',
        '@media (max-width: 599px)': {
            padding: '16px 16px 14px 16px',
        },
    },
    heroName: {
        fontFamily: 'Rubik',
        fontSize: 20,
        fontWeight: 600,
        lineHeight: '130%',
        color: '#FFFFFF',
    },
    heroDescription: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        lineHeight: '150%',
        color: 'rgba(255, 255, 255, 0.8)',
        marginTop: 4,
    },

    // ── Progression bars ────────────────────────────
    progressSection: {
        padding: '0 20px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 8,
        marginBottom: 16,
        '@media (max-width: 599px)': {
            padding: '0 12px',
        },
    },
    progressRow: {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        gap: 8,
    },
    progressBarBg: {
        flex: 1,
        height: 6,
        borderRadius: 3,
        backgroundColor: CapeeshColors.Gray5,
        overflow: 'hidden',
    },
    progressBarFill: {
        height: '100%',
        borderRadius: 3,
        transition: 'width 600ms ease-in-out',
    },
    progressLabel: {
        fontFamily: 'Rubik',
        fontSize: 11,
        fontWeight: 500,
        color: CapeeshColors.Gray1,
        minWidth: 32,
        textAlign: 'right' as const,
    },

    // ── Lesson list ─────────────────────────────────
    lessonsHeader: {
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 600,
        color: CapeeshColors.DarkText,
        padding: '0 20px',
        marginBottom: 10,
        '@media (max-width: 599px)': {
            padding: '0 12px',
        },
    },
    lessonList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 10,
        padding: '0 20px 32px 20px',
        '@media (max-width: 599px)': {
            padding: '0 12px 32px 12px',
        },
    },

    // ── Loading ─────────────────────────────────────
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
        gap: 12,
    },
    loadingText: {
        fontFamily: 'Rubik',
        fontSize: 13,
        color: CapeeshColors.Gray1,
    },
}))

// ─── Types ────────────────────────────────────────────────────────

interface TopicDetailViewProps {
    topic: CourseMetadataV1Dto
    courseGroupId: string
    l1Id: number
    lessonOrder: CourseLessonOrderDto | null
    lessonOrderProgression: CourseLessonOrderProgressionDto | null
    fetchingLessonOrder: boolean
    fetchingLessonOrderProgression: boolean
    dispatch: (action: any) => void
    onBack: () => void
    isNativeSpeaker?: boolean
}

// ─── Helpers ──────────────────────────────────────────────────────

function buildLessonProgressionMap(
    progression: CourseLessonOrderProgressionDto | null
): Record<string, CourseLessonOrderItemProgressionDto> {
    const map: Record<string, CourseLessonOrderItemProgressionDto> = {}
    if (!progression?.courseLessonOrderItemProgressions) return map
    progression.courseLessonOrderItemProgressions.forEach((lp) => {
        if (!lp) return
        // Key by customLessonId if available, otherwise by exerciseId
        const key = lp.customLessonId || (lp.exerciseId != null ? String(lp.exerciseId) : null)
        if (key) {
            map[key] = lp
        }
    })
    return map
}

// ─── Component ────────────────────────────────────────────────────

const TopicDetailView: React.FC<TopicDetailViewProps> = ({
    topic,
    courseGroupId,
    l1Id,
    lessonOrder,
    lessonOrderProgression,
    fetchingLessonOrder,
    fetchingLessonOrderProgression,
    dispatch,
    onBack,
    isNativeSpeaker = false,
}) => {
    const classes = useStyles()
    const intl = useIntl()

    // Fetch the topic hero image via the inline hook
    const topicImageBlobUrl = useStudentImage(topic?.imageUrl)

    // Fetch lesson order and progression on mount
    useEffect(() => {
        if (topic?.topicId != null && courseGroupId && l1Id) {
            dispatch(studentCourseActions.getCourseLessonOrder(courseGroupId, l1Id, topic.topicId))
            dispatch(studentCourseActions.getCourseLessonOrderProgression(courseGroupId, l1Id, topic.topicId))
        }
    }, [topic?.topicId, courseGroupId, l1Id])

    const lessonProgressionMap = useMemo(() => {
        return buildLessonProgressionMap(lessonOrderProgression)
    }, [lessonOrderProgression])

    const topicName = topic?.l2TopicName || topic?.l1TopicName || intl.formatMessage(studentScreenMessages.topicFallback)
    const topicDescription = topic?.l2TopicDescription || topic?.l1TopicDescription || ''
    const starsAttained = lessonOrderProgression?.starsAttained ?? 0
    const starsTotal = lessonOrderProgression?.starsAttainableInCourse ?? 0
    const normalizedProgression = lessonOrderProgression?.courseNormalizedProgression ?? 0
    const progressPercent = Math.min(Math.round(normalizedProgression * 100), 100)
    const isComplete = normalizedProgression >= 0.999

    const lessons = lessonOrder?.courseLessonOrderItems || []
    const sortedLessons = useMemo(() => {
        return [...lessons].sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
    }, [lessons])

    const isLoading = fetchingLessonOrder && sortedLessons.length === 0

    const handleLessonSelect = (lesson: CourseLessonOrderItemDto) => {
        if (!lesson.customLessonId) return

        // Look up progression for this lesson to pass stars to the brief page
        const progKey = lesson.customLessonId ||
            (lesson.exerciseId != null ? String(lesson.exerciseId) : null)
        const prog = progKey ? lessonProgressionMap[progKey] || null : null

        history.push(`/student/lesson/${lesson.customLessonId}`, {
            courseGroupId,
            l1Id,
            topicId: topic.topicId,
            customLessonId: lesson.customLessonId,
            generationIndex: undefined,
            stars: prog?.stars ?? 0,
            normalizedHighScore: prog?.normalizedHighScore ?? 0,
            isNativeSpeaker,
        })
    }

    return (
        <div className={classes.root}>
            {/* Nav bar with back arrow and stars */}
            <div className={classes.navBar}>
                <IconButton
                    className={classes.backButton}
                    onClick={onBack}
                    size="small"
                    aria-label={intl.formatMessage(studentScreenMessages.back)}
                >
                    <ArrowBackIcon />
                </IconButton>
                <div className={classes.navStarBadge}>
                    <Typography className={classes.navStarText}>
                        {starsAttained}/{starsTotal}
                    </Typography>
                    <StarIcon className={classes.navStarIcon} />
                </div>
            </div>

            {/* Hero section */}
            <div className={classes.heroSection}>
                <img
                    src={topicImageBlobUrl || DefaultCourseGroupImage}
                    className={classes.heroImage}
                    alt={topicName}
                />
                <div className={classes.heroContent}>
                    <Typography className={classes.heroName}>
                        {topicName}
                    </Typography>
                    {topicDescription && (
                        <Typography className={classes.heroDescription}>
                            {topicDescription}
                        </Typography>
                    )}
                </div>
            </div>

            {/* Progression bar */}
            <div className={classes.progressSection}>
                <div className={classes.progressRow}>
                    <div className={classes.progressBarBg}>
                        <div
                            className={classes.progressBarFill}
                            style={{
                                width: `${progressPercent}%`,
                                backgroundColor: isComplete
                                    ? CapeeshColors.GreenBright
                                    : CapeeshColors.Primary600,
                            }}
                        />
                    </div>
                    <Typography className={classes.progressLabel}>
                        {progressPercent}%
                    </Typography>
                </div>
            </div>

            {/* Lesson list */}
            {isLoading ? (
                <div className={classes.loadingContainer}>
                    <CircularProgress size={28} style={{ color: CapeeshColors.Primary600 }} />
                    <Typography className={classes.loadingText}>
                        {intl.formatMessage(studentScreenMessages.loadingLessons)}
                    </Typography>
                </div>
            ) : (
                <>
                    {sortedLessons.length > 0 && (
                        <Typography className={classes.lessonsHeader}>
                            {intl.formatMessage(studentScreenMessages.lessons)}
                        </Typography>
                    )}
                    <div className={classes.lessonList}>
                        {sortedLessons.map((lesson: CourseLessonOrderItemDto, idx: number) => {
                            // Build the key for matching progression
                            const progKey = lesson.customLessonId ||
                                (lesson.exerciseId != null ? String(lesson.exerciseId) : null)
                            const lessonProgression = progKey
                                ? lessonProgressionMap[progKey] || null
                                : null

                            return (
                                <LessonCard
                                    key={lesson.tempId || lesson.customLessonId || idx}
                                    lesson={lesson}
                                    progression={lessonProgression}
                                    onSelect={handleLessonSelect}
                                    index={idx}
                                />
                            )
                        })}
                    </div>
                </>
            )}
        </div>
    )
}

export default TopicDetailView
